// Slice 40 — primary/secondary/ghost/danger/outline button. Sizes sm|md|lg.
// Inline-style port of the design-handoff `HqButton` so the visual fidelity
// is byte-faithful to the mockup. Hover/active states are computed in JS
// so we don't need a runtime CSS-in-JS dep.

import type { ButtonHTMLAttributes, ComponentType, ReactNode, SVGProps } from "react";
import { useState } from "react";

type IconProps = SVGProps<SVGSVGElement> & { sw?: number };
export type IconComponent = ComponentType<IconProps>;

export type HqButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "outline";
export type HqButtonSize = "sm" | "md" | "lg";

export interface HqButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: HqButtonVariant;
  size?: HqButtonSize;
  leadingIcon?: IconComponent;
  trailingIcon?: IconComponent;
  loading?: boolean;
  children?: ReactNode;
}

const SIZES: Record<
  HqButtonSize,
  { h: number; px: number; fs: number; gap: number; iconSize: number }
> = {
  sm: { h: 28, px: 10, fs: 11.5, gap: 6, iconSize: 12 },
  md: { h: 34, px: 12, fs: 12.5, gap: 8, iconSize: 13 },
  lg: { h: 40, px: 16, fs: 13.5, gap: 10, iconSize: 14 },
};

interface Variant {
  bg: string;
  bgHover: string;
  color: string;
  border: string;
}

const VARIANTS: Record<HqButtonVariant, Variant> = {
  primary: {
    bg: "var(--hq-accent)",
    bgHover: "#3fdcf2",
    color: "#04101a",
    border: "none",
  },
  secondary: {
    bg: "var(--hq-bg-active)",
    bgHover: "#22262f",
    color: "var(--hq-text)",
    border: "1px solid var(--hq-border-strong)",
  },
  ghost: {
    bg: "transparent",
    bgHover: "var(--hq-bg-active)",
    color: "var(--hq-text-muted)",
    border: "1px solid transparent",
  },
  danger: {
    bg: "var(--hq-danger-soft)",
    bgHover: "rgba(251,113,133,.18)",
    color: "var(--hq-danger)",
    border: "1px solid rgba(251,113,133,.25)",
  },
  outline: {
    bg: "transparent",
    bgHover: "var(--hq-bg-hover)",
    color: "var(--hq-text)",
    border: "1px solid var(--hq-border-strong)",
  },
};

export function HqButton({
  variant = "secondary",
  size = "md",
  leadingIcon: L,
  trailingIcon: R,
  loading = false,
  disabled,
  type = "button",
  children,
  style,
  ...rest
}: HqButtonProps) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const s = SIZES[size];
  const v = VARIANTS[variant];
  const isDisabled = disabled || loading;
  return (
    <button
      {...rest}
      type={type}
      disabled={isDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.h,
        padding: `0 ${s.px}px`,
        borderRadius: "var(--hq-radius-lg)",
        fontSize: s.fs,
        fontWeight: 500,
        background: hover && !isDisabled ? v.bgHover : v.bg,
        color: v.color,
        border: v.border,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        transition: "background .12s, opacity .12s, transform .06s",
        transform: active && !isDisabled ? "translateY(1px)" : "none",
        ...style,
      }}
    >
      {loading ? (
        <Spinner size={s.iconSize} />
      ) : (
        L && <L width={s.iconSize} height={s.iconSize} sw={1.8} />
      )}
      {children}
      {R && !loading && <R width={s.iconSize} height={s.iconSize} sw={1.8} />}
    </button>
  );
}

function Spinner({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="hq-spin"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity=".25" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export interface HqIconButtonProps extends Omit<HqButtonProps, "leadingIcon" | "trailingIcon"> {
  icon: IconComponent;
  title?: string;
}

export function HqIconButton({
  icon: I,
  size = "md",
  variant = "ghost",
  style,
  title,
  ...rest
}: HqIconButtonProps) {
  const dim = { sm: 24, md: 30, lg: 36 }[size];
  return (
    <HqButton
      variant={variant}
      size={size}
      title={title}
      {...rest}
      style={{ width: dim, height: dim, padding: 0, ...style }}
    >
      <I width={size === "sm" ? 12 : 14} height={size === "sm" ? 12 : 14} sw={1.7} />
    </HqButton>
  );
}
